<template>
	<el-dialog :visible.sync="visible" title="Notice" width="520px" append-to-body custom-class="herp-sysnotice" :close-on-click-modal="false" @close="onClose">
		<div v-for="notice in notices" :key="notice.notice_id" class="herp-sysnotice-item">
			<div class="herp-sysnotice-title">{{ notice.title }}</div>
			<div class="herp-sysnotice-content">{{ notice.content }}</div>
		</div>
		<div slot="footer" class="herp-sysnotice-footer">
			<el-checkbox v-model="hide_today" class="herp-sysnotice-hide">Don't show again today</el-checkbox>
			<el-button type="primary" size="small" class="herp-sysnotice-close" @click="visible = false">Close</el-button>
		</div>
	</el-dialog>
</template>

<script>
// WK-20261021-02 : 전 회사 공통 시스템 공지. App.vue 에 한 번 두어 MainPage 밖의 독립 라우트까지 포함한다.
// - 로그인 화면(라우트 이름 login): 오늘 숨긴 공지를 빼고 표시하며, 실제로 표시한 공지만 이 탭의 seen 으로 남긴다.
// - 그 밖의 화면: 이 탭에 로그인 직후 pending 이 있을 때만 표시한다(Login.vue 가 로그인 성공 때 남김).
//   응답을 처리하거나 조회가 실패하면 pending 을 지운다. 응답 전에 화면이 바뀌거나 새로고침되면 남겨 두어 다음 화면에서 다시 시도한다.
// - 처리마다 토큰을 올려, 화면이 바뀐 뒤 도착한 응답은 표시·seen·pending 을 건드리지 않고 버린다.
// - 라우터가 초기 이동을 마치기 전의 START 라우트는 처리하지 않는다(onReady 뒤 확정 라우트만).
// - 열린 모달은 경로(path)가 바뀌거나 현재 조회 결과가 표시할 것 없음(빈 목록·실패·전부 억제)이면 닫는다.
//   쿼리만 바뀌는 이동(화면이 URL 쿼리를 갱신하는 경우)에는 방금 띄운 공지를 유지한다.
//   자동으로 닫을 때는 "Don't show again today" 기록을 남기지 않는다.
import { apiSystemNotices } from '@/api/systemNotice';
import { browserStorage, clearPendingIf, hideToday, localToday, markSeen, readPending, visibleIds } from '@/utils/systemNotice';

export default {
	name: 'SystemNoticeHost',
	props: {
		// 시험에서 탭별 저장소를 넣기 위한 것. 앱에서는 브라우저 저장소를 쓴다.
		storageProvider: { type: Function, default: browserStorage },
	},
	data() {
		return {
			visible: false,
			notices: [],
			shown_ids: [],
			hide_today: false,
		};
	},
	watch: {
		$route(to) {
			if (this.router_ready) this.handleRoute(to);
		},
	},
	created() {
		this.request_token = 0;
		this.router_ready = false;
		this.host_destroyed = false;
		this.last_route = null;
	},
	mounted() {
		this.$router.onReady(() => {
			if (this.host_destroyed) return;
			this.router_ready = true;
			this.handleRoute(this.$route);
		});
	},
	beforeDestroy() {
		this.host_destroyed = true;
		this.request_token++;
	},
	methods: {
		handleRoute(route) {
			// 초기 이동은 onReady 와 $route 감시가 같은 route 객체로 두 번 알린다.
			if (route === this.last_route) return;
			const path_changed = !this.last_route || !route || this.last_route.path !== route.path;
			this.last_route = route;
			if (path_changed) this.dismiss();
			const token = ++this.request_token;
			if (!route || !route.matched || route.matched.length === 0) return;
			const session = this.storageProvider('sessionStorage');
			if (route.name === 'login') {
				this.loadNotices(token, 'login', null, session);
				return;
			}
			const pending = readPending(session);
			if (pending === null) return;
			this.loadNotices(token, 'after-login', pending, session);
		},
		async loadNotices(token, page, pending, session) {
			let rows = null;
			try {
				const response = await apiSystemNotices();
				rows = response && response.data && Array.isArray(response.data.notices) ? response.data.notices : [];
			} catch (e) {
				rows = null;
			}
			if (this.host_destroyed || token !== this.request_token) return;
			if (page === 'after-login') clearPendingIf(session, pending);
			if (!rows || rows.length === 0) {
				this.dismiss();
				return;
			}

			const by_id = {};
			const ids = [];
			rows.forEach(row => {
				if (!row || row.notice_id === undefined || row.notice_id === null) return;
				const key = String(row.notice_id);
				if (by_id[key]) return;
				by_id[key] = {
					notice_id: key,
					title: row.title == null ? '' : String(row.title),
					content: row.content == null ? '' : String(row.content),
				};
				ids.push(key);
			});
			const shown = visibleIds(ids, page, localToday(new Date()), this.storageProvider('localStorage'), session);
			if (shown.length === 0) {
				this.dismiss();
				return;
			}
			if (page === 'login') markSeen(shown, session);
			this.notices = shown.map(key => by_id[key]);
			this.shown_ids = shown;
			this.hide_today = false;
			this.visible = true;
		},
		// 사용자가 닫지 않은 자동 정리. 체크 상태를 먼저 풀어 오늘 숨김을 남기지 않는다.
		dismiss() {
			if (!this.visible) return;
			this.hide_today = false;
			this.visible = false;
		},
		onClose() {
			if (this.hide_today) hideToday(this.shown_ids, localToday(new Date()), this.storageProvider('localStorage'));
		},
	},
};
</script>

<style>
.herp-sysnotice .el-dialog__body {
	max-height: 60vh;
	overflow-y: auto;
	padding-top: 10px;
	color: #222;
	font-size: 14px;
	line-height: 1.5;
}
.herp-sysnotice-item + .herp-sysnotice-item {
	border-top: 1px solid #ddd;
	margin-top: 14px;
	padding-top: 14px;
}
.herp-sysnotice-title {
	font-size: 16px;
	font-weight: bold;
	margin-bottom: 8px;
}
.herp-sysnotice-content {
	white-space: pre-line;
	word-wrap: break-word;
	overflow-wrap: break-word;
}
.herp-sysnotice-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
}
</style>
